/* eslint-env browser */
import logger from '../logger'
import {
  get,
  isPlainObject,
} from 'lodash-es'
import strings from './strings.js'
import metrics from '@rei/metrics'
import ItemUtils from '../utils/item-utils'
import CouponUtils from '../utils/coupon-utils'
import FulfillmentUtils from '../utils/fulfillment-utils'
import { sha256 } from '../utils/hash-utils';

export default {
  init: function() {
    const isTestEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';
    if (!isTestEnv) {
      window?.metrics?.init({ logToConsole: true }) || metrics.init({ logToConsole: true });
    }

    // send the version of checkout the user has loaded along to analytics.
    // This should allow us to see if users browsers are caching the /checkout
    // endpoint or if they are getting the latest checkout. Useful for
    // crossreferencing especially in the case of a pre/post test
    const version = this.getCheckoutVersion();
    if (version) {
      this.sendClick(`checkout:version ${version}`);
    }
  },
  sendView: function sendViewFunc(data) {
    logger(`ANALYTICS :: sending view event, ${data}`, 'debug')()
    window?.metrics?.view(data) || metrics.view(data);
  },

  sendClick: function sendClickFunc(data, e) {
    logger(`ANALYTICS :: sending click event ${data} on ${e}`, 'debug')()
    window?.metrics?.link(data) || metrics.link(data);
  },

  /**
   * This method grabs the version of checkout from the name of the javascript bundle.
   * The Regex is specifically looking for the file name with regex with the following logic:
   *  - checkout- is matched literally but not captured.
   *  - ([^/]+) captures what follows (version hash).
   *  - (?=\.\w+$) is a positive lookahead to assert a file extension like .js.
   *
   * @return  {String}  a string matching the version derived from the file name or empty string if no match.
   */
  getCheckoutVersion: function getCheckoutVersion() {
    const targetScript = document.querySelector('#appEntry');
    if (!targetScript) return '';
    const match = /checkout-([^/]+)(?=\.\w+$)/gm.exec(targetScript.src);
    return match?.[1] ?? '';
  },

  getCheckoutType: function getCheckoutType(orderData, pageViewType) {
    let checkoutType = strings.checkoutType.guest
    const isApplePayExpress = pageViewType === 'applePayExpressConfirmation'
    const savedShippingAddresses = get(orderData, 'user.savedShippingAddresses', [])
    const isLoggedIn = this.getIsLoggedIn(orderData) === strings.isLoggedInTrue
    const tenders = get(orderData, 'tenders', []) || []
    let isPayPalExpress = false

    tenders.forEach((tender) => {
      if (tender.type === 'PAYPAL_EXPRESS') isPayPalExpress = true
    })

    if (isPayPalExpress) {
      checkoutType = strings.checkoutType.paypalExpress
    } else if (isApplePayExpress) {
      checkoutType = strings.checkoutType.applePayExpress
    } else if (isLoggedIn) {
      if (savedShippingAddresses && savedShippingAddresses.length > 0) {
        checkoutType = strings.checkoutType.signedInWithAddress
      } else {
        checkoutType = strings.checkoutType.signedInNoAddress
      }
    }
    return { checkoutType }
  },

  getMemberNumber: orderData => get(orderData, 'customerInfo.memberNumber'),

  getCustomerMemberStatus: (orderData) => {
    const memberType = get(orderData, 'customerInfo.memberType') === 'NOT_APPLICABLE' ? strings.nonMember : strings.member
    return memberType
  },

  getCustomerMemberType: (orderData) => {
    const memberType = get(orderData, 'customerInfo.memberType')
    return memberType
  },

  getIsLoggedIn: (orderData) => {
    const isLoggedIn = get(orderData, 'user.isGuest') ? strings.isLoggedInFalse : strings.isLoggedInTrue
    return isLoggedIn
  },

  getOnlineAccountNumber: orderData => get(orderData, 'user.reiRefId'),

  getUserEmail: orderData => orderData?.billingAddress?.address?.email?.trim(),

  getUserInfo: async function getUserInfo(orderData) {
    if (!isPlainObject(orderData)) {
      logger('ERROR :: (ANALYTICS) failed to get order info', 'error')()
      return {}
    }
    const hashedEmail = await sha256(this.getUserEmail(orderData)?.toLowerCase() || null);
    const hashedUpperCaseEmail = await sha256(this.getUserEmail(orderData)?.toUpperCase() || null);
    return {
      memberNumber: this.getMemberNumber(orderData),
      customerMemberStatus: this.getCustomerMemberStatus(orderData),
      isLoggedIn: this.getIsLoggedIn(orderData),
      onlineAccountNumber: this.getOnlineAccountNumber(orderData),
      memberStatusDetail: this.getCustomerMemberType(orderData),
      hashedEmail,
      hashedUpperCaseEmail,
    }
  },

  getProductData: function getProductData(orderData, attribute) {
    let productData = ''
    const lineItems = get(orderData, 'lineItems', [])

    lineItems.forEach((item, index) => {
      if (index !== 0) productData += ','
      // Style isn't actually a product attribute
      if (attribute === 'style') {
        productData += get(item, 'sku').substring(0, 6)
      } else {
        productData += get(item, attribute)
      }
    })
    return productData
  },

  getProductString: function getProductString(orderData, pageViewType) {
    let products = ''
    const lineItems = get(orderData, 'lineItems', [])

    lineItems.forEach((item, index) => {
      const sku = get(item, 'sku', false)
      const style = sku.substring(0, 6)
      const quantity = get(item, 'quantity', false)
      const unitPrice = get(item, 'price.unitAdjusted', false)
      const lineItemPrice = get(item, 'price.totalAdjusted', false)
      const shippingPrice = get(item, 'price.shippingChargeAdjusted', false)
      const shipNode = get(item, 'fulfillment.fulfillmentInfos[0].shipNode', false)
      const eadEpoch = get(item, 'fulfillment.fulfillmentInfos[0].ead', -1)
      const coupon = CouponUtils.getAppliedManualCouponNameByItemId(orderData, item.reiRefId)
      const membersOnly = ItemUtils.isJustForMembersItem(item) ? 'member only' : false
      // this is kind of gross, but we want to first check if we're getting Sterling data from audit
      // and if not fall back to the calculated-available-shipping-options data
      const restrictions = (get(item, 'fulfillment.restrictions', []) || []).length
        ? (get(item, 'fulfillment.restrictions', []) || [])
        : (get(item, 'fulfillments.restrictions', []) || [])
      const unavailable = FulfillmentUtils.isUnavailable(restrictions)
      const shipMethods = orderData.safeGet(`getters.fulfillmentsModel[${item.reiRefId}].fulfillments[0].shipMethodsInformation.allowedShipMethods`, [])
      let orderShippingName = get(item, 'shipMethod.id', false)
      let giftCardType = ''
      let ead = ''
      let ffdEad = ''


      if (orderShippingName === 'SHIP_TO_STORE') {
        const levelOfService = get(item, 'fulfillment.levelOfService', false)
        if (levelOfService) {
          orderShippingName = levelOfService
        }
      }

      if (eadEpoch > -1) {
        const eadDate = new Date(eadEpoch)
        ead = `${eadDate.getFullYear()}-${eadDate.getMonth() + 1}-${eadDate.getDate()}`
      }

      // This is the EAD that is displayed on the shipping options page coming from `dates-by-service`.
      // It can be different from the EAD above from `cart` that shows on the review page. We want to
      // track them both to see how often and how much of a difference there is.
      if (shipMethods.length) {
        const shipMethod = shipMethods.find(method => method.id === orderShippingName)
        const ffdEpoch = get(shipMethod, 'fulfillmentInfos[0].ead')

        if (ffdEpoch) {
          const ffdDate = new Date(ffdEpoch)
          ffdEad = `${ffdDate.getFullYear()}-${ffdDate.getMonth() + 1}-${ffdDate.getDate()}`
        }
      }

      if (ItemUtils.isGiftCard(item)) {
        if (get(item, 'scheduledDeliveryDate') > Date.now()) {
          giftCardType = 'Scheduled Gift Card'
        } else if (ItemUtils.isDigitalGiftCard(item)) {
          giftCardType = 'E Gift Card'
        } else if (ItemUtils.isPhysicalGiftCard(item)) {
          giftCardType = 'Physical Gift Card'
        } else if (ItemUtils.isPromoGiftCard(item)) {
          giftCardType = 'Promo Card'
        }
      }

      if (index !== 0) products += ','
      // Baseline product data
      products += `;${style};${quantity};${lineItemPrice};`
      // Events; events are only included on the order confirmation page view
      if (pageViewType === 'confirmation') products += `event38=${shippingPrice}|event44=${unitPrice}`
      // eVars
      products += `;eVar84=${sku}`
      if (orderShippingName) products += `|eVar4=${orderShippingName}`
      if (coupon) products += `|eVar74=${coupon}`
      if (ead) products += `|eVar83=${ead}`
      if (ffdEad) products += `|evar164=${ffdEad}`
      if (shipNode) products += `|eVar85=${shipNode}`
      if (giftCardType) products += `|eVar95=${giftCardType}`
      if (membersOnly) products += `|eVar79=${membersOnly}`
      if (unavailable) products += '|eVar158=unavailable'
    })
    return products
  },

  parseProductInfo: function parseProductInfo(orderData, pageViewType) {
    if (!isPlainObject(orderData)) {
      logger('ERROR :: (ANALYTICS) failed to get order info', 'error')()
      return {}
    }
    return {
      productQuantities: this.getProductData(orderData, 'quantity'),
      productSkus: this.getProductData(orderData, 'sku'),
      productStyles: this.getProductData(orderData, 'style'),
      productPrices: this.getProductData(orderData, 'price.unitAdjusted'),
      products: this.getProductString(orderData, pageViewType),
      productBrands: this.getProductData(orderData, 'brandName'),
    }
  },
}
